import {makeAutoObservable} from 'mobx';
import LibraryRepository, {
  EditChunkOption,
} from '../../../data/repositories/LibraryRepository';
import LoadingModule from '../../../modules/LoadingModule/LoadingModule';
import {toast} from 'react-toastify';
import {getErrorFromCode} from '../../../data/errorCodes';
import {toastConfig} from '../../../uiToolkit/Toast/toastConfig';
import {GeneratedVideoChunk} from '../../../data/models/LibraryFile/GeneratedVideoChunk';
import {VideoEditorText} from '../VideoEditorText';

export enum VideoEditorState {
  Idle = 0,
  // the codes below match enum LibraryMediaFileStatus from the backend
  MutingAudio = 20,
  Regenerating = 21,
}

class VideoEditorStore {
  private libraryRepository = new LibraryRepository();
  private loadingModule = new LoadingModule();
  public editChunkErrorCode = '';
  public state: VideoEditorState = VideoEditorState.Idle;

  constructor() {
    makeAutoObservable(this);
  }

  public get isLoading() {
    return this.loadingModule.isLoading;
  }

  public async getVideoChunks(organizationId: string, mediaFileId: string) {
    this.loadingModule.startLoading();
    const result =
      await this.libraryRepository.getVideoChunksForGeneratedContent(
        organizationId,
        mediaFileId,
      );
    this.loadingModule.endLoading();
    if (result.success && result.data) {
      const uniqueStatuses = Array.from(
        new Set(result.data.map(f => f.fileStatus)),
      );
      if (uniqueStatuses.find(s => s == VideoEditorState.Regenerating)) {
        this.state = VideoEditorState.Regenerating;
      } else if (uniqueStatuses.find(s => s == VideoEditorState.MutingAudio)) {
        this.state = VideoEditorState.MutingAudio;
      } else if (uniqueStatuses.every(x => x === 0)) {
        this.state = VideoEditorState.Idle;
      }
      return result.data;
    } else if (result.errors && result.errors.length) {
      throw new Error(getErrorFromCode(result.errors[0]));
    }
    return [];
  }

  public getMediaFile = async (organizationId: string, mediaFileId: string) => {
    const result = await this.libraryRepository.getByContentId(
      organizationId,
      mediaFileId,
    );
    if (result.success && result.data) {
      return result.data;
    }
  };

  public async updateVideoChunksForGeneratedContent(
    organizationId: string,
    mediaFileId: string,
    chunks: GeneratedVideoChunk[],
  ) {
    this.loadingModule.startLoading();
    const result =
      await this.libraryRepository.updateVideoChunksForGeneratedContent(
        organizationId,
        mediaFileId,
        chunks,
      );
    this.loadingModule.endLoading();
    if (result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return result.success;
  }

  public async compileVideoChunks(organizationId: string, mediaFileId: string) {
    this.loadingModule.startLoading();
    const result = await this.libraryRepository.compileVideoFile(
      organizationId,
      mediaFileId,
    );
    this.loadingModule.endLoading();
    if (!result.success && result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    } else if (result.success) {
      toast.success(VideoEditorText.publishSuccess, toastConfig);
    }
    return result.success;
  }

  public async EditChunk(
    organizationId: string,
    mediaFileId: string,
    option: EditChunkOption,
    chunkId: string,
  ) {
    this.loadingModule.startLoading();
    switch (option) {
      case EditChunkOption.Regenerate:
        this.state = VideoEditorState.Regenerating;
        break;
      case EditChunkOption.MuteAudio:
        this.state = VideoEditorState.MutingAudio;
        break;
      case EditChunkOption.EditScript:
      case EditChunkOption.Delete:
      default:
        this.state = VideoEditorState.Idle;
        break;
    }
    const result = await this.libraryRepository.editChunk(
      organizationId,
      mediaFileId,
      option,
      chunkId,
    );
    this.loadingModule.endLoading();
    if (result.success) {
      //this.editVideoErrorMessage = editVideoSourceText.regenerateSuccess;
    } else if (result.errors && result.errors.length) {
      this.editChunkErrorCode = result.errors[0];
      this.state = VideoEditorState.Idle;
    }
    return result.success;
  }
}

export default VideoEditorStore;
